import React, { FunctionComponent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  DropdownItem,
  InputField,
  TextToggler,
} from 'plume-ui';
import useFormControls from '../../../hooks/useFormControls';
import OutreachConfigSection from '../../../OutreachConfigSection';
import {
  ConfigurationCTAUrlResponse,
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  SubscriberContentFormFields,
} from '../../../../../types';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import {
  activePartnerAtom,
  outreachTestMessageModalAtom,
  selectedCtaUrl,
} from '../../../../../../../state';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import useValidation from '../../../hooks/useValidation';
import { OutreachTypes } from '../../../../../../../urls';
import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';
import { Permission } from '../../../../../../../userPermissionConfig';
import {
  getMaxLengthFactory,
  getRemainingCharactersFactory,
} from '../../../helpers';
import { useFetchData } from '../../../../../../../hooks/useFetchData';
import { DependencyContainer } from '../../../../../../../DependencyContainer';

export type FormikSubscriberOutreachContentValue = {
  outreachRuleName: string;
  subject: string;
  message: string;
  urlEnabled: boolean;
  urlLinkReference: string;
  urlText: string;
  urlLink: string;
  subscriberOutreachTime?: {
    from?: string;
    to?: string;
  };
};

const { outreachService } = new DependencyContainer();

const FormikSubscriberOutreachContentField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSubscriberOutreachContentValue
>> = ({ field, form }) => {
  const { t } = useTranslation();
  const {
    setPropertyValue,
    getPropertyValue,
    toggleFieldValue,
  } = useFormControls<FormikSubscriberOutreachContentValue>(field, form);
  const { hasPermission } = useFeaturePermission();

  const canEditSubscriberPush = hasPermission(
    Permission.EditSubscriberPushOutreach,
  );
  const titleCopyId = 'outreach.form.subscriberOutreachContentSection.title';
  const subtitleCopyId =
    'outreach.form.subscriberOutreachContentSection.subtitle';
  const { isSectionComplete } = useValidation();
  const setOutreachTestMessageModal = useSetRecoilState(
    outreachTestMessageModalAtom,
  );
  const isComplete = isSectionComplete(
    OutreachConfigSections.SubscriberOutreachContent,
  );
  const activePartner = useRecoilValue(activePartnerAtom);
  const [urlDropdownLabel, setUrlDropdownLabel] = useState<string>(
    t('outreach.form.subscriberOutreachContentSection.urlDropdownLabel'),
  );
  const currentCtaUrl = useRecoilValue(selectedCtaUrl);
  const setSelectedCtaUrl = useSetRecoilState(selectedCtaUrl);
  const [loadingUrls, urlList] = useFetchData<
    ConfigurationCTAUrlResponse[],
    any
  >(
    async () => {
      if (!activePartner?.id) return;
      const listItems = await outreachService.getCTAUrls(activePartner.id);
      return listItems;
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  useEffect(() => {
    if (!urlList) return;
    const selected = urlList?.find(
      (recipient) =>
        recipient.urlLink === field.value[SubscriberContentFormFields.UrlLink],
    );
    setUrlDropdownLabel(
      selected?.urlLink ||
        currentCtaUrl?.urlLink ||
        t('outreach.form.subscriberOutreachContentSection.urlDropdownLabel'),
    );
    setSelectedCtaUrl(selected);
  }, [urlList]);

  useEffect(() => {
    if (currentCtaUrl === undefined) return;
    setPropertyValue(SubscriberContentFormFields.UrlLink, currentCtaUrl?.id);
  }, [currentCtaUrl]);

  const onClickTestNotification = () => {
    setOutreachTestMessageModal({
      isOpen: true,
      messageType: 'issue',
      outreachType: OutreachTypes.Push,
      ctaUrl: currentCtaUrl,
    });
  };

  const shouldShowURLContent = Boolean(
    getPropertyValue(SubscriberContentFormFields.UrlEnabled),
  );

  const getRemainingCharactersFor = getRemainingCharactersFactory(field);
  const getMaxLengthFor = getMaxLengthFactory(field);

  const remainingSubjectLength = getRemainingCharactersFor(
    SubscriberContentFormFields.Subject,
  );
  const remainingMessageLength = getRemainingCharactersFor(
    SubscriberContentFormFields.Message,
  );

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSubscriberPush}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__outreach-content-row OutreachContainer__outreach-content-row--horizontal-form">
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.outreachRuleName" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SubscriberContentFormFields.OutreachRuleName}
                type="text"
                value={
                  field.value[SubscriberContentFormFields.OutreachRuleName]
                }
                onInput={(e) =>
                  setPropertyValue(
                    SubscriberContentFormFields.OutreachRuleName,
                    e.currentTarget.value,
                  )
                }
              />
              <OutreachConfigFieldMessages
                propertyName={SubscriberContentFormFields.OutreachRuleName}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachContentSection.subject" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SubscriberContentFormFields.Subject}
                type="text"
                value={field.value[SubscriberContentFormFields.Subject]}
                maxLength={getMaxLengthFor(SubscriberContentFormFields.Subject)}
                onInput={(e) =>
                  setPropertyValue(
                    SubscriberContentFormFields.Subject,
                    e.currentTarget.value,
                  )
                }
              />
              <div className="OutreachContainer__input-footer">
                <FormattedMessage
                  id="charactersRemaining"
                  params={{
                    characters: remainingSubjectLength,
                  }}
                />
              </div>
              <OutreachConfigFieldMessages
                propertyName={SubscriberContentFormFields.Subject}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.subscriberOutreachContentSection.message" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                maxLength={getMaxLengthFor(SubscriberContentFormFields.Message)}
                onInput={(e) => {
                  setPropertyValue(
                    SubscriberContentFormFields.Message,
                    e.currentTarget.value,
                  );
                }}
                value={field.value[SubscriberContentFormFields.Message]}
              />
              <div className="OutreachContainer__input-footer">
                <FormattedMessage
                  id="charactersRemaining"
                  params={{
                    characters: remainingMessageLength,
                  }}
                />
              </div>
              <OutreachConfigFieldMessages
                propertyName={SubscriberContentFormFields.Message}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.subscriberOutreachContentSection.url" />
            </div>

            <div className="OutreachContainer__outreach-content-field-content">
              <div className="OutreachContainer__outreach-content-field-content-row">
                <TextToggler
                  title={t(
                    'outreach.form.subscriberOutreachContentSection.enabled',
                  )}
                  selected={
                    getPropertyValue(
                      SubscriberContentFormFields.UrlEnabled,
                    ) as boolean
                  }
                  onToggle={() =>
                    toggleFieldValue(SubscriberContentFormFields.UrlEnabled)
                  }
                />
              </div>

              {shouldShowURLContent && (
                <Dropdown
                  label={loadingUrls ? t('loading') : urlDropdownLabel}
                  expandDirection="auto"
                  closeOnItemClick
                  openInPortal
                  noItemsWarning={t('noData')}
                >
                  {urlList?.map((ctaUrl) => (
                    <DropdownItem
                      key={ctaUrl.id}
                      onClick={() => {
                        setUrlDropdownLabel(ctaUrl.urlLink);
                        setSelectedCtaUrl(
                          urlList?.find((url) => url.id === ctaUrl.id),
                        );
                      }}
                    >
                      {ctaUrl.urlLink}
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.supportOutreachContentSection.test" />
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              <Button
                styleVariant="quaternary"
                onClick={() => onClickTestNotification()}
              >
                <FormattedMessage id="outreach.form.supportOutreachContentSection.testNotification" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSubscriberOutreachContentField);
